import { createElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/pro-solid-svg-icons';
import * as sharpLightIcons from '@fortawesome/sharp-light-svg-icons';
import * as duotoneIcons from '@fortawesome/duotone-light-svg-icons';
import * as brandIcons from '@fortawesome/free-brands-svg-icons';

const fallbackIcon = solidIcons.faCircleQuestion || solidIcons.faCircleInfo;

const resolveIcon = (icons, names, label) => {
  for (const name of names) {
    if (icons[name]) {
      return icons[name];
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[icons] Missing Font Awesome icon for ${label}. Tried: ${names.join(', ')}`);
  }

  return fallbackIcon;
};

const getFontSize = (size) => {
  if (typeof size === 'number') {
    return `${size}px`;
  }

  if (typeof size === 'string' && size.length > 0) {
    return size;
  }

  return undefined;
};

const createIcon = (icon, baseStyle) => {
  const IconComponent = ({ size = 16, stroke, style, ...props }) => {
    // Keep `stroke` accepted for compatibility with previous icon API.
    void stroke;

    const fontSize = getFontSize(size);
    const merged = { ...baseStyle, ...(fontSize ? { fontSize } : undefined), ...style };
    const iconStyle = Object.keys(merged).length > 0 ? merged : undefined;

    return createElement(FontAwesomeIcon, {
      ...props,
      icon,
      style: iconStyle,
    });
  };

  return IconComponent;
};

const DUOTONE_STYLE = {
  '--fa-secondary-color': 'var(--mantine-color-gray-3)',
  '--fa-secondary-opacity': 1,
};

const createDuotoneIcon = (icon) => createIcon(icon, DUOTONE_STYLE);

// ---------------------------------------------------------------------------
// Sharp-light icons (default style)
// ---------------------------------------------------------------------------
export const IconAddressBook = createIcon(resolveIcon(sharpLightIcons, ['faAddressBook'], 'IconAddressBook'));
export const IconAddressCard = createIcon(resolveIcon(sharpLightIcons, ['faAddressCard'], 'IconAddressCard'));
export const IconAddressCardSolid = createIcon(resolveIcon(solidIcons, ['faAddressCard'], 'IconAddressCardSolid'));
export const IconAlertCircle = createIcon(resolveIcon(sharpLightIcons, ['faCircleExclamation'], 'IconAlertCircle'));
export const IconAlertTriangle = createIcon(resolveIcon(sharpLightIcons, ['faTriangleExclamation'], 'IconAlertTriangle'));
export const IconArrowLeft = createIcon(resolveIcon(sharpLightIcons, ['faArrowLeft'], 'IconArrowLeft'));
export const IconAt = createIcon(resolveIcon(sharpLightIcons, ['faAt'], 'IconAt'));
export const IconBracketsCurly = createIcon(resolveIcon(sharpLightIcons, ['faBracketsCurly'], 'IconBracketsCurly'));
export const IconAtSolid = createIcon(resolveIcon(solidIcons, ['faAt'], 'IconAtSolid'));
export const IconCalendar = createIcon(resolveIcon(sharpLightIcons, ['faCalendarDays', 'faCalendar'], 'IconCalendar'));
export const IconDeviceFloppy = createIcon(resolveIcon(sharpLightIcons, ['faFloppyDisk'], 'IconDeviceFloppy'));
export const IconFlag = createIcon(resolveIcon(sharpLightIcons, ['faFlag'], 'IconFlag'));
export const IconGenderBigender = createIcon(resolveIcon(sharpLightIcons, ['faTransgender'], 'IconGenderBigender'));
export const IconGenderFemale = createIcon(resolveIcon(sharpLightIcons, ['faVenus'], 'IconGenderFemale'));
export const IconGenderMale = createIcon(resolveIcon(sharpLightIcons, ['faMars'], 'IconGenderMale'));
export const IconHome = createIcon(resolveIcon(sharpLightIcons, ['faHouse', 'faHome'], 'IconHome'));
export const IconHourglass = createIcon(
  resolveIcon(sharpLightIcons, ['faHourglassHalf', 'faHourglass', 'faHourglassClock'], 'IconHourglass')
);
export const IconInfoCircle = createIcon(resolveIcon(sharpLightIcons, ['faCircleInfo', 'faInfoCircle'], 'IconInfoCircle'));
export const IconKey = createIcon(resolveIcon(sharpLightIcons, ['faKey'], 'IconKey'));
export const IconLogout = createIcon(resolveIcon(sharpLightIcons, ['faRightFromBracket', 'faSignOutAlt'], 'IconLogout'));
export const IconMapPin = createIcon(resolveIcon(sharpLightIcons, ['faLocationDot', 'faMapPin'], 'IconMapPin'));
export const IconMapPinSolid = createIcon(resolveIcon(solidIcons, ['faLocationDot', 'faMapPin'], 'IconMapPinSolid'));
export const IconMobile = createIcon(resolveIcon(sharpLightIcons, ['faMobileScreenButton', 'faMobileScreen', 'faMobile'], 'IconMobile'));
export const IconMobileSolid = createIcon(resolveIcon(solidIcons, ['faMobileScreenButton', 'faMobileScreen', 'faMobile'], 'IconMobileSolid'));
export const IconMovie = createIcon(resolveIcon(sharpLightIcons, ['faFilm'], 'IconMovie'));
export const IconPen = createIcon(resolveIcon(sharpLightIcons, ['faPen'], 'IconPen'));
export const IconPenToSquare = createIcon(resolveIcon(sharpLightIcons, ['faPenToSquare', 'faEdit'], 'IconPenToSquare'));
export const IconPhone = createIcon(resolveIcon(sharpLightIcons, ['faPhone'], 'IconPhone'));
export const IconPhoneSolid = createIcon(resolveIcon(solidIcons, ['faPhone'], 'IconPhoneSolid'));
export const IconPhoto = createIcon(resolveIcon(sharpLightIcons, ['faImage'], 'IconPhoto'));
export const IconVideo = createIcon(resolveIcon(sharpLightIcons, ['faVideo'], 'IconVideo'));
export const IconPlus = createIcon(resolveIcon(sharpLightIcons, ['faPlus'], 'IconPlus'));
export const IconRefresh = createIcon(resolveIcon(sharpLightIcons, ['faRotateRight', 'faArrowsRotate', 'faSync'], 'IconRefresh'));
export const IconRulerMeasure = createIcon(resolveIcon(sharpLightIcons, ['faRulerCombined'], 'IconRulerMeasure'));
export const IconSettings = createIcon(resolveIcon(sharpLightIcons, ['faGear', 'faCog'], 'IconSettings'));
export const IconShareNodes = createIcon(resolveIcon(sharpLightIcons, ['faShareNodes', 'faShareAlt'], 'IconShareNodes'));
export const IconSparkles = createIcon(resolveIcon(sharpLightIcons, ['faSparkles', 'faStars'], 'IconSparkles'));
export const IconThumbDown = createIcon(resolveIcon(sharpLightIcons, ['faThumbsDown'], 'IconThumbDown'));
export const IconThumbUp = createIcon(resolveIcon(sharpLightIcons, ['faThumbsUp'], 'IconThumbUp'));
export const IconTimes = createIcon(resolveIcon(sharpLightIcons, ['faXmark', 'faTimes'], 'IconTimes'));
export const IconTrash = createIcon(resolveIcon(sharpLightIcons, ['faTrashCan', 'faTrash'], 'IconTrash'));
export const IconUser = createIcon(resolveIcon(sharpLightIcons, ['faUser'], 'IconUser'));
export const IconUserCircle = createIcon(resolveIcon(sharpLightIcons, ['faCircleUser', 'faUserCircle'], 'IconUserCircle'));
export const IconWeightScale = createIcon(resolveIcon(sharpLightIcons, ['faWeightScale', 'faBalanceScale'], 'IconWeightScale'));
export const IconWorld = createIcon(resolveIcon(sharpLightIcons, ['faGlobe', 'faEarthAmericas'], 'IconWorld'));
export const IconEnvelope = createIcon(resolveIcon(sharpLightIcons, ['faEnvelope'], 'IconEnvelope'));
export const IconEnvelopeSolid = createIcon(resolveIcon(solidIcons, ['faEnvelope'], 'IconEnvelopeSolid'));

// ---------------------------------------------------------------------------
// Solid icons (used where a filled style is needed)
// ---------------------------------------------------------------------------
export const IconCirclePlus = createIcon(resolveIcon(solidIcons, ['faCirclePlus', 'faPlusCircle'], 'IconCirclePlus'));
export const IconCircleUserSolid = createIcon(resolveIcon(solidIcons, ['faCircleUser', 'faUserCircle'], 'IconCircleUserSolid'));
export const IconPenSolid = createIcon(resolveIcon(solidIcons, ['faPen'], 'IconPenSolid'));

// ---------------------------------------------------------------------------
// Duotone icons
// ---------------------------------------------------------------------------
export const IconEnvelopeDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faEnvelope'], 'IconEnvelopeDuotone'));
export const IconMobileDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faMobileScreenButton', 'faMobileScreen', 'faMobile'], 'IconMobileDuotone'));
export const IconAddressCardDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faAddressCard'], 'IconAddressCardDuotone'));
export const IconMapPinDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faLocationDot', 'faMapPin'], 'IconMapPinDuotone'));
export const IconAtDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faAt'], 'IconAtDuotone'));
export const IconPhoneDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faPhone'], 'IconPhoneDuotone'));
export const IconUserDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faUser'], 'IconUserDuotone'));
export const IconSparklesDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faSparkles', 'faStars'], 'IconSparklesDuotone'));
export const IconShareNodesDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faShareNodes', 'faShareAlt'], 'IconShareNodesDuotone'));
export const IconWeightScaleDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faWeightScale', 'faBalanceScale'], 'IconWeightScaleDuotone'));
export const IconInfoCircleDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faCircleInfo', 'faInfoCircle'], 'IconInfoCircleDuotone'));
export const IconVideoDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faVideo'], 'IconVideoDuotone'));
export const IconPhotoDuotone = createDuotoneIcon(resolveIcon(duotoneIcons, ['faImage'], 'IconPhotoDuotone'));
// ---------------------------------------------------------------------------
// Brand icons
// ---------------------------------------------------------------------------
export const IconBrandBehance = createIcon(resolveIcon(brandIcons, ['faBehance'], 'IconBrandBehance'));
export const IconBrandFacebook = createIcon(resolveIcon(brandIcons, ['faFacebookF', 'faFacebook'], 'IconBrandFacebook'));
export const IconBrandInstagram = createIcon(resolveIcon(brandIcons, ['faInstagram'], 'IconBrandInstagram'));
export const IconBrandLinkedin = createIcon(resolveIcon(brandIcons, ['faLinkedinIn', 'faLinkedin'], 'IconBrandLinkedin'));
export const IconBrandPinterest = createIcon(resolveIcon(brandIcons, ['faPinterestP', 'faPinterest'], 'IconBrandPinterest'));
export const IconBrandTiktok = createIcon(resolveIcon(brandIcons, ['faTiktok'], 'IconBrandTiktok'));
export const IconBrandVimeo = createIcon(resolveIcon(brandIcons, ['faVimeoV', 'faVimeo'], 'IconBrandVimeo'));
export const IconBrandX = createIcon(resolveIcon(brandIcons, ['faXTwitter', 'faTwitter'], 'IconBrandX'));
export const IconBrandYoutube = createIcon(resolveIcon(brandIcons, ['faYoutube'], 'IconBrandYoutube'));
